"use client";

import { Button } from "@heroui/react";
import { motion, useReducedMotion, type TargetAndTransition } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import { SCHEDULE_DEMO_URL } from "@/lib/site-config";

type Tone = "light" | "accent" | "dark";

interface Plan {
  name: string;
  tone: Tone;
  badge?: string;
  description: string;
  /** Headline figure ("$29.990" or "A medida"). */
  price: string;
  /** Subtle inline unit beside the figure; omitted for custom quotes. */
  priceUnit?: string;
  cta: string;
  features: string[];
}

const plans: Plan[] = [
  {
    name: "Starter",
    tone: "light",
    description: "Para tus primeros contratistas.",
    price: "$29.990",
    priceUnit: "CLP / mes",
    cta: "Agendar una demo",
    features: [
      "Hasta 5 contratistas",
      "Hasta 50 trabajadores",
      "Plataforma completa desde el día uno",
      "Veri, copiloto con IA, incluido",
    ],
  },
  {
    name: "Growth",
    tone: "accent",
    badge: "Recomendado",
    description: "Para redes con varios proyectos.",
    price: "$79.990",
    priceUnit: "CLP / mes",
    cta: "Agendar una demo",
    features: [
      "Hasta 20 contratistas",
      "Hasta 400 trabajadores",
      "Todo lo de Starter",
      "Pensado para operar varios proyectos a la vez",
    ],
  },
  {
    name: "Enterprise",
    tone: "dark",
    description: "Para operaciones de alto volumen.",
    price: "A medida",
    cta: "Solicitar levantamiento",
    features: [
      "Más de 20 contratistas",
      "Volumen de trabajadores a medida",
      "Todo lo de Growth",
      "Costo según levantamiento",
    ],
  },
];

/** Per-tone styling: light glass (Starter), brand band (Growth), dark teal (Enterprise). */
const tones: Record<
  Tone,
  {
    card: string;
    name: string;
    panel: string;
    description: string;
    price: string;
    priceNote: string;
    button: string;
    feature: string;
    check: string;
  }
> = {
  light: {
    card: "bg-white/60 backdrop-blur-md border border-white ring-1 ring-dark-teal/[0.04] hover:ring-accent/30 hover:bg-white/80",
    name: "text-dark-teal",
    panel: "bg-white ring-1 ring-dark-teal/[0.06] shadow-[0_1px_2px_rgba(21,41,47,0.04),0_10px_28px_rgba(21,41,47,0.05)]",
    description: "text-dark-teal/55",
    price: "text-dark-teal",
    priceNote: "text-dark-teal/45",
    button:
      "border border-dark-teal/12 bg-white text-dark-teal/80 hover:border-accent/40 hover:bg-white hover:text-dark-teal",
    feature: "text-dark-teal/70",
    check: "text-accent",
  },
  accent: {
    card: "bg-white/60 backdrop-blur-md border border-white ring-1 ring-dark-teal/[0.04] hover:ring-accent/30 hover:bg-white/80",
    name: "text-white",
    panel: "bg-white ring-1 ring-dark-teal/[0.06] shadow-[0_1px_2px_rgba(21,41,47,0.05),0_14px_36px_rgba(101,129,234,0.16)]",
    description: "text-dark-teal/55",
    price: "text-dark-teal",
    priceNote: "text-dark-teal/45",
    button: "btn-glow bg-accent text-white hover:bg-[#5570e0]",
    feature: "text-dark-teal/70",
    check: "text-accent",
  },
  dark: {
    card: "bg-dark-teal border border-white/10 ring-1 ring-white/[0.06] hover:ring-light-teal/40",
    name: "text-white",
    panel: "bg-white/[0.06] ring-1 ring-white/10",
    description: "text-white/55",
    price: "text-white",
    priceNote: "text-white/50",
    button: "bg-white text-dark-teal hover:bg-white/90",
    feature: "text-white/70",
    check: "text-light-teal",
  },
};

function openDemo() {
  window.open(SCHEDULE_DEMO_URL, "_blank", "noopener,noreferrer");
}

const VIEWPORT = { once: true, margin: "-60px" } as const;

function PlanCard({ plan }: { plan: Plan }) {
  const t = tones[plan.tone];
  const isAccent = plan.tone === "accent";
  // Skip the inner micro-animations when the OS asks for reduced motion; the
  // outer ScrollReveal still handles the card's fade-in.
  const reduceMotion = useReducedMotion();
  const enter = (from: TargetAndTransition) => (reduceMotion ? false : from);

  return (
    <article
      aria-label={`Plan ${plan.name}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-[22px] p-1 transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-y-1 motion-reduce:hover:translate-y-0 ${t.card}`}
    >
      {/* Brand band behind the recommended plan's header. Its height equals
          card inset + header + panel radius, so in the side gutters the
          gradient hugs the panel's rounded corners (inverse-radius look). */}
      {isAccent && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-[64px] bg-linear-to-r from-[#6581EA] via-[#6497D6] to-[#67B4C1] mx-1 mt-[3px] rounded-t-[20px]"
        />
      )}

      {/* Header row */}
      <div className="relative flex h-[44px] items-center justify-between px-4">
        <h3 className={`text-[17px] font-semibold tracking-tight ${t.name}`}>
          {plan.name}
        </h3>
        {plan.badge && (
          <motion.span
            initial={enter({ opacity: 0, scale: 0.85, y: 3 })}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ type: "spring", stiffness: 380, damping: 26, delay: 0.55 }}
            className="rounded-full bg-white px-2.5 py-1 text-[11.5px] font-semibold tracking-[-0.005em] text-accent shadow-[0_1px_2px_rgba(21,41,47,0.08)]"
          >
            {plan.badge}
          </motion.span>
        )}
      </div>

      {/* Elevated panel: description, price, CTA */}
      <div className={`relative rounded-[22px] p-4 lg:p-[18px] ${t.panel}`}>
        <p className={`text-[14px] leading-[1.5] ${t.description}`}>
          {plan.description}
        </p>

        <div className="mt-2.5 mb-5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span
            className={`text-[36px] font-medium leading-none tracking-[-0.03em] tabular-nums lg:text-[40px] ${t.price}`}
          >
            {plan.price}
          </span>
          {plan.priceUnit && (
            <span className={`whitespace-nowrap text-[13px] font-medium tracking-[-0.01em] ${t.priceNote}`}>
              {plan.priceUnit}
            </span>
          )}
        </div>

        <Button
          onPress={openDemo}
          className={`group/cta flex h-11 w-full items-center justify-center gap-2.5 rounded-2xl px-5 text-[14px] font-semibold tracking-[-0.01em] transition-colors ${t.button}`}
        >
          {plan.cta}
          <ArrowRight
            className="h-4 w-4 opacity-70 transition-transform duration-300 ease-out group-hover/cta:translate-x-0.5 motion-reduce:transition-none"
            strokeWidth={2}
          />
        </Button>
      </div>

      {/* Feature list */}
      <ul className="relative flex flex-col gap-1.5 px-4 pb-3 pt-[22px]">
        {plan.features.map((feature, i) => (
          <motion.li
            key={feature}
            initial={enter({ opacity: 0, x: -6 })}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.45, delay: 0.35 + i * 0.06, ease: [0.25, 0.1, 0.25, 1] }}
            className={`flex items-start gap-2.5 text-[14px] leading-[1.45] ${t.feature} ${
              i < 2 ? "font-semibold" : ""
            }`}
          >
            <Check
              className={`mt-[3px] h-4 w-4 shrink-0 ${t.check}`}
              strokeWidth={2.4}
            />
            <span>{feature}</span>
          </motion.li>
        ))}
      </ul>
    </article>
  );
}

export default function PricingSection() {
  return (
    <section
      id="precios"
      aria-label="Planes y precios de Verify"
      className="px-6 pt-16 sm:pt-20 pb-16 sm:pb-20"
    >
      <div className="mx-auto max-w-[60rem] text-center mb-12">
        <ScrollReveal>
          <span className="inline-block rounded-full px-3.5 py-1 text-sm sm:text-base font-semibold text-accent mb-0">
            Precios
          </span>
        </ScrollReveal>
        <ScrollReveal delay={0.08}>
          <h2 className="flex flex-col text-balance font-medium text-[2rem] sm:text-[36px] lg:text-5xl tracking-[-0.02em] text-dark-teal leading-[1.1] sm:leading-[1] mb-4">
            <span>Precios simples.</span>
            <span className="text-[#a6a6ab]">Sin letra chica.</span>
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={0.15}>
          <p className="text-pretty text-[.95rem] sm:text-[18px] leading-[1.6] text-dark-teal/50 max-w-[540px] mx-auto">
            Elige según el tamaño de tu red de contratistas. Sube de plan cuando
            tu operación crezca.
          </p>
        </ScrollReveal>
      </div>

      <div className="mx-auto max-w-[60rem] grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
        {plans.map((plan, i) => (
          <ScrollReveal key={plan.name} delay={i * 0.1} className="h-full">
            <PlanCard plan={plan} />
          </ScrollReveal>
        ))}
      </div>

      <ScrollReveal delay={0.3}>
        <p className="mt-8 text-center text-[13px] text-dark-teal/40">
          Precios mensuales en pesos chilenos. ¿No sabes qué plan te calza?{" "}
          <a
            href="#demo"
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById("demo")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="font-medium text-accent/80 underline-offset-4 transition-colors hover:text-accent hover:underline"
          >
            Lo vemos con tu caso en una demo.
          </a>
        </p>
      </ScrollReveal>
    </section>
  );
}
